/**
 * Vendor Guard — protects vendor portal routes.
 *
 * Checks if a vendor session exists via VendorAuthService and that the
 * token has not expired. If not authenticated or expired, clears session
 * and redirects to /vendor/sign-in with the intended URL as ?redirect=.
 *
 * In development, allows stub auth via a query param:
 * /vendor/dashboard?stub=vendorId&name=Costa&color=%2300a651
 */
import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { VendorAuthService } from '../services/vendor-auth.service';
import { environment } from '../../../environments/environment';

export const vendorGuard: CanActivateFn = (route, state) => {
  const vendorAuth = inject(VendorAuthService);
  const router = inject(Router);

  // Development stub: allow bypassing auth via query params
  if (!environment.production) {
    const stubVendorId = route.queryParamMap.get('stub');
    if (stubVendorId) {
      const name = route.queryParamMap.get('name') || 'Dev Vendor';
      const color = route.queryParamMap.get('color') || '#6366f1';
      vendorAuth.stubAuth(stubVendorId, name, color);
      return true;
    }
  }

  // Check if authenticated and token is not expired
  if (vendorAuth.isAuthenticated()) {
    return true;
  }

  // Token expired — clear the stale session
  if (vendorAuth.isExpired()) {
    vendorAuth.signOut();
  }

  // Redirect to sign-in, preserving the intended destination
  const redirectUrl = state.url;
  return router.createUrlTree(['/vendor/sign-in'], {
    queryParams: redirectUrl && redirectUrl !== '/vendor' ? { redirect: redirectUrl } : {},
  });
};
